import React, { HTMLProps, useEffect, useState } from 'react'
import { AxiosRequestConfig } from 'axios'
import { SelectType } from 'tennders-common/types/SelectType'
import Trans from 'next-translate/Trans'
import useAutocomplete from 'tennders-common/form/hooks/useAutocomplete'
import Suggestions from 'tennders-common/form/components/new-inputs/Suggestions'
import stylesInput from '@/styles/Input.module.scss'

type Props = Omit<HTMLProps<HTMLInputElement>, 'onSet' | 'onTextSet' | 'value' | 'name'> & {
  name?: string,
  value ?: any,
  onSet ?: (value: any) => void,
  url ?: string,
  items ?: SelectType[],
  error ?: string,
  className ?: string,
  required ?: boolean,
  readOnly ?: boolean,
  suggestionStyle ?: React.CSSProperties,
  requestBuilder ?: ({ keyword, options }: {keyword: string, options: AxiosRequestConfig}) => Promise<any>,
  dataExporter ?: (data: Record<string, any>) => Record<string, any>,
  tagPillClassName ?: string
}

const MultipleItemPicker = (props: Props) => {
  const {
    value,
    onSet,
    url,
    requestBuilder: propsRequestBuilder,
    items,
    error,
    className,
    dataExporter,
    readOnly,
    suggestionStyle = {},
    tagPillClassName,
    ...rest
  } = props
  const {
    text,
    setSuggestions,
    suggestions,
    onTextChanged,
    handleSuggestionSelect,
    suggestionsLoading,
    showNoDataPrompt
  } = useAutocomplete({ dataExtractor: dataExporter, url, requestBuilder: propsRequestBuilder, items, value, onSet })
  const [tags, setTags] = useState([])

  const removeTag = (item) => {
    const result = tags.filter((tag) => tag.id !== item.id)
    setTags(result)
    if (onSet) {
      onSet(result)
    }
  }

  const onTagSelect = (item) => {
    const identicalItems = tags?.find((tag) => tag.id === item.id)
    if (!identicalItems) {
      setTags([...tags, item])
      handleSuggestionSelect([...tags, item])
    }
  }

  useEffect(() => {
    if (value?.length > 0) {
      setTags(value)
    }
    if (value?.length === 0) {
      setTags([])
    }
  }, [value])

  return (
    <>
      <div className={`${className || 'form-input w-100'}${error ? ' error' : ''}${tags?.length > 0 ? ' selected' : ''} tag-pill-wrapper`}>
        {tags.map((tag) => (
          <div key={`tag_${tag.id}`} className={'tag-pill d-flex align-center ' + (tagPillClassName || '')}>
            <span className="tag-pill-text">{tag.label}</span>
            <span className="tag-pill-close" onClick={() => removeTag(tag)}><img src="/img/icon-trash.svg" alt="" width="16" height="16" /></span>
          </div>
        ))}
        <input
          disabled={!!readOnly}
          readOnly={!!readOnly}
          value={text}
          onChange={onTextChanged}
          type="text"
          className="tag-pill-input"
          {...rest}
        />
      </div>
      {suggestions?.length > 0
        ? (
          <Suggestions
            onOut={null}
            handleSuggestionSelect={onTagSelect}
            suggestions={suggestions}
            style={suggestionStyle}
          />
        )
        : (
          text && !suggestionsLoading && showNoDataPrompt && (
            <div className="d-flex pos-rel">
              <ul style={suggestionStyle} className={stylesInput['autocomplete-ul']}>
                <li>
                  <span><Trans i18nKey="variables:no_items_found" /></span>
                </li>
              </ul>
            </div>
          ))}
    </>
  )
}

export default React.memo(MultipleItemPicker)
